import {connect} from 'pwa-helpers';
import {LitElement, html} from 'lit';
import {customElement} from 'lit/decorators.js';
import {msg} from '@lit/localize';
import {navigator, router} from 'lit-element-router';
import {repeat} from 'lit/directives/repeat.js';
import deepEqual from 'deep-equal';
import store from '../../store';
import {
	getProfile,
	selectProfileById,
	updateProfileCV,
} from '../../slices/profileSlice';
import style from '../../../../scss/artistCv.scss';
import formPageStyle from '../../../../scss/formPage.scss';
import linkstyle from '../../../../scss/link.scss';
import loadingSpinnerStyle from '../../../../scss/loadingSpinner.scss';
import getRoutes from '../../routes';
import {
	selectCurrentContentLanguage, selectCurrentProfile,
} from '../../selectors';
import {deepClone} from '../../util/deepClone';
import {navigateTo, setFormChanged} from '../../slices/statusSlice';
import up from '../../icons/up';
import down from '../../icons/down';
import {isPermitted} from '../../util/isPermitted';
import {profileDisplayName} from '../../util/profileDisplayName';

@customElement('arc-artist-cv')
export class ArtistCv extends connect(store)(router(navigator(LitElement))) {
	static styles = [formPageStyle, linkstyle, loadingSpinnerStyle, style];

	static properties = {
		localFormData: {type: Object, hasChanged: (n, o) => !deepEqual(n, o)},
		errors: {type: Object, hasChanged: (n, o) => !deepEqual(n, o)},
		edit: {type: Boolean},
	};

	constructor() {
		super();
		this.localFormData = null;
		this.errors = null;
		this.isLoading = false;
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.formChanged = state.status.formChanged;
		this.errors = state.profile.error;
		this.isLoading = state.profile.loading === 'loading';
		this.profile = selectCurrentProfile(state);

		this.permittedToViewProfile = isPermitted(['profile/view'], this.profileId, state);
		this.permittedToEditProfile = isPermitted(['profile/edit'], this.profileId, state);
		this.permittedToViewArtworks = isPermitted(['artwork/view'], this.profileId, state);
		this.permittedToEditArtworks = isPermitted(['artwork/edit'], this.profileId, state);

		this.contentLanguage = selectCurrentContentLanguage(state);
	}

	static get routes() {
		return getRoutes();
	}

	async router(route, params, query, data) {
		this.params = params;

		this.edit = true; //params.mode === 'edit';
		if (route === 'artist-cv' && this.profileId !== params.id) {
			this.profileId = params.id;
			await store.dispatch(getProfile({id: this.profileId})); // fetch profile from backend
			// clone data for local editing (component state)
			this.localFormData = deepClone(selectProfileById(this.state, this.profileId).cv);
		}
		this.requestUpdate();
	}

	editCv() {
		// edit mode
		store.dispatch(navigateTo({route: 'artist-cv', params: {...this.params, mode: 'edit'}}));
	}

	back() {
		// cancel editing and go back to preview
		store.dispatch(navigateTo({route: 'artist', params: {...this.params, mode: 'preview'}}));
	}

	handleValueChange(index, value) {
		// update localFormData with new value in section and field
		this.localFormData[index] = value; // update field
		this.requestUpdate('localFormData');
		store.dispatch(setFormChanged(this.formHasChanged()));
	}
	handleRemoveSection(index) {
		// remove section at index
		this.localFormData.splice(index, 1); // remove section
		this.requestUpdate('localFormData');
		store.dispatch(setFormChanged(this.formHasChanged()));
	}
	handleAddSection() {
		// add section at end
		this.localFormData.push({
			sectionName: [
				{value: '', lang: 'de'},
				{value: '', lang: 'en'},
			],
			entries: [],
		}); // add section
		this.requestUpdate('localFormData');
		store.dispatch(setFormChanged(this.formHasChanged()));
	}

	connectedCallback() {
		super.connectedCallback();
		store.dispatch(setFormChanged(false));
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		store.dispatch(setFormChanged(false));
	}

	// saveProfile: Edit user profile data, send it to API
	async saveCv() {
		await store.dispatch(updateProfileCV({id: this.profileId, cv: this.localFormData}));
		store.dispatch(setFormChanged(false));
	}

	formHasChanged() {
		if (!this.localFormData) return false;

		// check if local form data is different
		const newData = deepClone(this.localFormData);
		const oldData = deepClone(selectProfileById(this.state, this.profileId).cv);
		return !deepEqual(newData, oldData);
	}

	sortCVSevtions(oldIndex, newIndex) {
		console.log('sortCVSevtions', oldIndex, newIndex);
	}

	moveSectionUp(index) {
		if (index > 0) {
			const updatedFormData = deepClone(this.localFormData);
			[updatedFormData[index - 1], updatedFormData[index]] = [
				updatedFormData[index],
				updatedFormData[index - 1],
			];
			this.localFormData = updatedFormData;
			store.dispatch(setFormChanged(this.formHasChanged()));
		}
	}

	moveSectionDown(index) {
		if (index < this.localFormData.length - 1) {
			const updatedFormData = deepClone(this.localFormData);
			[updatedFormData[index + 1], updatedFormData[index]] = [
				updatedFormData[index],
				updatedFormData[index + 1],
			];
			this.localFormData = updatedFormData;
			store.dispatch(setFormChanged(this.formHasChanged()));
		}
	}

	render() {
		const s = store.getState();

		if (this.isLoading || !this.localFormData) {
			return html`
				<div class="loading-overlay">
					<div class="spinner"></div>
				</div>
			`;
		}
		return html`
			<div class="formPage">
				<arc-toolbar class="toolbar">
					<div slot="left">
						<arc-breadcrumbs>
							<arc-routerlink route="artist">${msg('Profile')}: ${profileDisplayName(this.profile)}</arc-routerlink>
							<span>${msg('Curriculum Vitae')}</span>
						</arc-breadcrumbs>
						<arc-content-language-switch></arc-content-language-switch>
					</div>
					<div slot="right">
						<arc-button
							title="${msg('Save CV')}"
							type="primary"
							.additionalInfo="${false}"
							?disabled="${!this.formChanged || !this.permittedToEditProfile}"
							class="saveCvButton"
							@click=${this.saveCv}>
						</arc-button>
					</div>
				</arc-toolbar>


				<div class="leftRightColumns">

					<div class="leftColumn">
						<header>
							<h2 class="pageTitle">${msg('Curriculum Vitae')}</h2>
						</header>
					</div>
					<div class="rightColumn">
						<section>
							<div class="cvSectionList">
								${repeat(
									this.localFormData,
									(section, index) => 'section' + index,
									(section, index) => html`
										<div class="cvSectionContainer">
											<div class="cvSection">
												<arc-cv-section
													index=${index}
													.value=${section}
													.errors=${this.errors?.params?.cv?.params?.[index]}
													?disabled="${!this.permittedToEditProfile}"
													@value-change=${(e) => this.handleValueChange(e.detail.index, e.detail.value)}>
												</arc-cv-section>
											</div>
											<div class="listButtons">
												<arc-dropdown-menu>
													<arc-button
														title="${msg('Move Up')}"
														type="listMenu"
														.showIcon=${true}
														.showLabel=${true}
														?disabled=${index === 0 || !this.permittedToEditProfile}
														@click=${() => this.moveSectionUp(index)}>
														${up}
													</arc-button>

													<arc-button
														title="${msg('Move Down')}"
														type="listMenu"
														.showIcon=${true}
														.showLabel=${true}
														?disabled=${index === this.localFormData.length - 1 || !this.permittedToEditProfile}
														@click=${() => this.moveSectionDown(index)}>
														${down}
													</arc-button>

													<arc-button
														title="${msg('Remove')}"
														type="listMenu"
														.showIcon=${false}
														.showLabel=${true}
														?disabled=${section.entries.length !== 0}
														@click=${() => this.handleRemoveSection(index)}>
														${up}
													</arc-button>
												</arc-dropdown-menu>
											</div>
										</div>
									`
								)}
							</div>
							<arc-button
								title="${msg('Add Section')}"
								type="primary"
								.additionalInfo="${false}"
								class="addSectionButton"
								@click=${this.handleAddSection}
							>
							</arc-button>
						</section>

					</div>
				</div>

			</div>
		`;
	}
}
